/**
 * Centralized currency formatter — never hardcode "$" anywhere.
 *
 *   fmtMoney(amount, currency?)  → "$1,234" | "Cg 1.234" | ...
 *
 * `currency` defaults to the app's reporting currency (loaded from
 * /api/site-settings/public into localStorage on boot). Historical
 * transactions should always pass their own currency to preserve the
 * original amount's meaning.
 */

const DEFAULT_LOCALE_BY_CURRENCY = {
  USD: "en-US",
  XCG: "nl-NL",
  ANG: "nl-NL",
  EUR: "en-IE",
};

// Some currencies (e.g. XCG) may render as "XCG 1,234" via Intl. Provide a
// friendly override for locally-used codes.
const SYMBOL_OVERRIDE = {
  XCG: "Cg ",
  ANG: "NAƒ ",
};

export function getReportingCurrency() {
  try {
    return localStorage.getItem("pb_reporting_currency") || "USD";
  } catch {
    return "USD";
  }
}

export function setReportingCurrency(code) {
  try { localStorage.setItem("pb_reporting_currency", (code || "USD").toUpperCase()); } catch {}
}

export function fmtMoney(amount, currency, opts = {}) {
  const cur = (currency || getReportingCurrency() || "USD").toUpperCase();
  const num = Number(amount || 0);
  const maximumFractionDigits = opts.maximumFractionDigits ?? (Math.abs(num) >= 1000 ? 0 : 2);

  if (SYMBOL_OVERRIDE[cur]) {
    const body = new Intl.NumberFormat(DEFAULT_LOCALE_BY_CURRENCY[cur] || "en-US", {
      style: "decimal",
      maximumFractionDigits,
      minimumFractionDigits: 0,
    }).format(num);
    return `${SYMBOL_OVERRIDE[cur]}${body}`;
  }

  try {
    return new Intl.NumberFormat(DEFAULT_LOCALE_BY_CURRENCY[cur] || "en-US", {
      style: "currency",
      currency: cur,
      maximumFractionDigits,
      minimumFractionDigits: 0,
    }).format(num);
  } catch {
    return `${cur} ${num.toLocaleString()}`;
  }
}
